import { useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { Menu01Icon } from '@hugeicons/core-free-icons'
import { api } from '../lib/api'
import type { AdminIdentity } from '../types'
import { Buildings, File, FolderOpen, Handshake, ListBullets, X, type IconProps } from './ui/icons'
import { Button } from './ui/primitives'
import '../styles/admin-shell.css'

/** standard: left-aligned column, max 1200px, page padding. full: the page owns its width and padding (the workspace editor). */
export type AdminShellWidth = 'standard' | 'full'

type NavItem = { to: string; label: string; icon: ComponentType<IconProps> }

export const ADMIN_NAV_ITEMS: NavItem[] = [
  { to: '/admin/workspaces', label: 'Workspaces', icon: Buildings },
  { to: '/admin/partners', label: 'Partners', icon: Handshake },
  { to: '/admin/contracts', label: 'Contracts', icon: File },
  { to: '/admin/documents', label: 'Documents', icon: FolderOpen },
  { to: '/admin/catalog', label: 'Catalog', icon: ListBullets },
]

// One identity request per page load. Every admin route shares it.
let identityRequest: Promise<AdminIdentity> | null = null
function loadIdentity(): Promise<AdminIdentity> {
  identityRequest ??= api.loadAdminIdentity().catch((error: unknown) => {
    identityRequest = null
    throw error
  })
  return identityRequest
}

/**
 * Persistent admin navigation around every signed-in admin page. Without an admin
 * session (401), it renders the page alone so the page's own sign-in panel shows.
 * Local dev has no admin session, so dev keeps the shell, like the pages' own dev previews.
 */
export function AdminShell({ children, width = 'standard' }: { children: ReactNode; width?: AdminShellWidth }) {
  const [identity, setIdentity] = useState<AdminIdentity | null>(null)
  const [signedOut, setSignedOut] = useState(false)
  const [signingOut, setSigningOut] = useState(false)

  useEffect(() => {
    let active = true
    loadIdentity()
      .then((me) => { if (active) setIdentity(me) })
      .catch((error: Error & { status?: number }) => { if (active && error.status === 401 && !import.meta.env.DEV) setSignedOut(true) })
    return () => { active = false }
  }, [])

  async function signOut() {
    setSigningOut(true)
    try {
      await api.signOutAdmin()
    } catch {
      // The cookie may already be gone. The entry page asks for sign-in again either way.
    }
    identityRequest = null
    window.location.assign('/')
  }

  if (signedOut) return <>{children}</>
  return <AdminShellFrame width={width} identity={identity} signingOut={signingOut} onSignOut={() => void signOut()}>{children}</AdminShellFrame>
}

/** The shell layout without data loading, so the dev review page can render it with sample data. */
export function AdminShellFrame({ children, width = 'standard', identity, signingOut = false, onSignOut }: { children: ReactNode; width?: AdminShellWidth; identity: AdminIdentity | null; signingOut?: boolean; onSignOut: () => void }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const { pathname } = useLocation()
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => { setMenuOpen(false) }, [pathname])

  useEffect(() => {
    if (!menuOpen) return
    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      setMenuOpen(false)
      menuButtonRef.current?.focus()
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [menuOpen])

  return (
    <div className="admin-frame" data-width={width}>
      <a className="admin-frame-skip" href="#admin-content" onClick={(event) => { event.preventDefault(); contentRef.current?.focus() }}>Skip to Content</a>

      <header className="admin-frame-bar">
        <img src="/prentus-logo-black.png" alt="Prentus" className="admin-frame-logo" />
        <button ref={menuButtonRef} className="button button-secondary compact" type="button" aria-expanded={menuOpen} aria-controls="admin-frame-rail" onClick={() => setMenuOpen((open) => !open)}>
          {menuOpen ? <X size={16} aria-hidden="true" /> : <HugeiconsIcon icon={Menu01Icon} size={16} strokeWidth={1.8} aria-hidden="true" />}
          Menu
        </button>
      </header>

      <div id="admin-frame-rail" className="admin-frame-rail" data-open={menuOpen}>
        <div className="admin-frame-brand">
          <img src="/prentus-logo-black.png" alt="Prentus" className="admin-frame-logo" />
          <span>Partnerships</span>
        </div>

        <nav aria-label="Admin">
          <ul className="admin-frame-links">
            {ADMIN_NAV_ITEMS.map(({ to, label, icon: Icon }) => (
              <li key={to}>
                <NavLink to={to} className="admin-frame-link"><Icon size={18} aria-hidden="true" /><span>{label}</span></NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="admin-frame-account">
          {identity && (
            <div className="admin-frame-identity">
              <strong>{identity.name}</strong>
              <span title={identity.email}>{identity.email}</span>
            </div>
          )}
          <Button variant="secondary" compact full type="button" disabled={signingOut} onClick={onSignOut}>{signingOut ? 'Signing Out…' : 'Sign Out'}</Button>
        </div>
      </div>

      <div id="admin-content" ref={contentRef} className="admin-frame-content" tabIndex={-1}>
        <div className="admin-frame-column">{children}</div>
      </div>
    </div>
  )
}
